import { useState } from "react";
import { toast } from "react-toastify";
import Modal from "../modal/Modal.js";
import ToastItem from "../ToastItem.js";
import { useTicketContext } from "../../context/TicketContext.js";
import { useNavigate } from "react-router-dom";

const InvestCheckModal = ({ slug, onClose, initialValues = {}, formConfig = {} }) => {
  const navigate = useNavigate();
  const { checkProcessHandler, loading, sendMessage } = useTicketContext();

  const maskAccountNumber = (accountNumber) => {
    if (!accountNumber) return "";
    return accountNumber.replace(/\d(?=\d{4})/g, "X");
  };

  // Set initial formData with optional initial values or empty strings
  const [formData, setFormData] = useState({
    bank: initialValues.bank || "",
    bankAccount: initialValues.bankAccount || "",
    checkAmount: initialValues.checkAmount || "",
    comment: initialValues.comment || ""
  });
  
  const [isCheckProcessed, setIsCheckProcessed] = useState(false);
  const [errors, setErrors] = useState({}); // State to store errors for each field

  const handleRemoveComment = (keyToRemove) => {
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      updatedErrors[keyToRemove] = "";
      return updatedErrors;
    });
  };



  const handleInputChange = (event) => {
    const { name, value } = event.target;
    console.log("Changing value: ", name, value); // Log the change

    if (name === "bank") {
      setFormData((prev) => ({
        ...prev,
        bank: value,
        bankAccount: "",
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const bankOptions = formConfig.bank || [];
  const bankAccountMap = formConfig.bankAccountByBank || {};
  const bankAccountOptions = formData.bank
    ? bankAccountMap[formData.bank] || []
    : [];

  // Form field definitions (can be extended via formConfig prop)
  const formFields = [
    {
      name: "bank",
      label: "Bank",
      type: "dropdown",
      placeholder: "Select Bank",
      options: bankOptions,
      validation:
        formConfig.bankValidation ||
        ((value) => (!value ? "Please select a Bank." : null)),
    },
    {
      name: "bankAccount",
      label: "Bank Account",
      type: "dropdown",
      placeholder: "Select Account",
      options: bankAccountOptions,
      validation:
        formConfig.bankAccountValidation ||
        ((value) => {
          if (!value) return "Bank Account Number is required.";
          if (formData.bank && !bankAccountOptions.includes(value)) {
            return "Selected Bank Account does not match the chosen Bank.";
          }
          return null;
        }),
    },
    {
      name: "checkAmount",
      label: "Check Amount",
      placeholder: "Check Amount",
      validation: formConfig.checkAmountValidation || false
    }
  ];

  // Validate fields on submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Collect validation errors
    const newErrors = {};
    formFields.forEach((field) => {
      const error = field.validation(formData[field.name], formData);
      if (error) {
        newErrors[field.name] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await checkProcessHandler("Invest", formData);
      sendMessage(`Decision: Invest\nBank: ${formData?.bank || ''}\nCheck Amount: ${formData?.checkAmount || ''}\nBank Account: ${formData?.bankAccount || ''} \nComment: ${formData?.comment || ''}`)
      setIsCheckProcessed(true);
      navigate("/manage-ticket?page=1");

    } catch (error) {
      toast.error(error?.message ? error.message : error + "");
    }
  };

  return (
    <Modal
      title={"Invest the check"}
      onClose={() => {
        setIsCheckProcessed(false);
        onClose();
      }}
    >
      {isCheckProcessed ? (
        <div className="flex flex-col items-center justify-center h-380 space-y-5">
          <img
            src="/success-fill.svg"
            className="w-104 cursor-pointer"
            alt="success-icon"
          />
          <p className="font-21-31-400-2 w-566 text-center">
            Based upon your entered details, your check has been processed successfully.
          </p>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="px-4 my-2 py-2 flex relative flex-col space-y-5"
        >
          {loading && (
            <div className="inset-0 absolute w-full h-full bg-white flex items-center justify-center">
              <p className="font-16-21-400">Processing your request. Please wait...</p>
            </div>
          )}

          <div>
            {Object.entries(errors)
              .filter(([key, value]) => value)
              .map(([key, value]) => (
                <ToastItem
                  key={key}
                  item={value}
                  index={key}
                  onRemove={handleRemoveComment}
                  type="error"
                />
              ))}
          </div>

          <div className="flex w-full flex-wrap">
            {formFields.map((field) => (
              <div key={field.name} className="flex w-1/2 px-2 flex-col my-2">
                <label className="mb-2 font-20-30-400" htmlFor={field.name}>
                  {field.label}
                </label>
                {field.type === "dropdown" ? (
                  <select
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleInputChange}
                    className="styled-dropdown"
                    disabled={field.name === "bankAccount" && !formData.bank}
                  >
                    <option value="" disabled>
                      {field.placeholder}
                    </option>
                    {(field.options || []).map((option, idx) => (
                      <option key={idx} value={option}>
                        {field.name === "bankAccount"
                          ? maskAccountNumber(option)
                          : option}
                      </option>
                    ))}
                  </select>


                ) : (
                  <input
                    type="text"
                    name={field.name}
                    placeholder={field.placeholder}
                    className={`border-input font-16-21-400 font-normal px-3 h-46 br-6 ${
                      errors[field.name] && "border-red"
                    }`}
                    value={formData[field.name]}
                    onChange={handleInputChange}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <div className="flex space-x-4">
            <div className="py-2 w-135">
              <button
                type="submit"
                className="border-none w-full h-49 font-16-21-700 capitalize justify-center rounded-md bg-[#3C394D] px-8 text-sm text-white shadow-sm sm:w-auto"
              >
                Submit
              </button>
            </div>

            <div className="py-2 w-135">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="border-none w-full h-49 font-16-21-700 capitalize justify-center rounded-md bg-cancel-2 px-8 text-sm text-white sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default InvestCheckModal;
